import type {
  BorrowerPair,
  ComplianceFinding,
  DisclosureDocument,
  DisclosureHistoryEntry,
  IntegrationError,
  IntegrationErrorKind,
  LoanRecord,
  ReadinessCheck,
  ValidationIssue,
} from '../types/disclosures'

export const loan: LoanRecord = {
  loanNumber: '2026-001284',
  primaryBorrower: 'John Smith',
  street: '123 Main Street',
  cityStateZip: 'Denver, CO 80202',
  amount: '$425,000',
  purpose: 'Purchase',
  rate: '6.375%',
  packageType: 'Three-Day / Initial',
}

export const borrowerPairs: BorrowerPair[] = [
  {
    id: 'pair-1',
    label: 'John Smith + Jane Smith',
    recipients: [
      {
        id: 'john',
        name: 'John Smith',
        role: 'Primary Borrower',
        email: 'john.smith@email.com',
        phone: '(303) 555-0123',
        delivery: 'Electronic',
        required: true,
      },
      {
        id: 'jane',
        name: 'Jane Smith',
        role: 'Co-Borrower',
        email: 'jane.smith@email.com',
        phone: '(303) 555-0198',
        delivery: 'Electronic',
        required: true,
      },
    ],
  },
  {
    id: 'pair-2',
    label: 'Michael Smith + Rachel Smith',
    recipients: [
      {
        id: 'michael',
        name: 'Michael Smith',
        role: 'Primary Borrower',
        email: 'michael.smith@email.com',
        phone: '(303) 555-0144',
        delivery: 'Electronic',
        required: true,
      },
      {
        id: 'rachel',
        name: 'Rachel Smith',
        role: 'Co-Borrower',
        email: 'rachel.smith@email.com',
        phone: '(303) 555-0177',
        delivery: 'Electronic',
        required: true,
      },
    ],
  },
]

export const readinessChecks: ReadinessCheck[] = [
  { id: 'borrower', label: 'Borrower information complete', state: 'passed' },
  { id: 'package', label: 'Required disclosure package identified', state: 'passed' },
  { id: 'loan', label: 'Loan data validated', state: 'passed' },
  { id: 'compliance', label: 'Compliance checks passed', state: 'passed' },
  { id: 'ready', label: 'Ready to generate', state: 'passed' },
]

export const validationIssues: ValidationIssue[] = [
  {
    id: 'email',
    title: 'Borrower email missing',
    description: 'John Smith requires an email address for electronic delivery.',
    actionLabel: 'Edit Borrower',
  },
  {
    id: 'vesting',
    title: 'Property vesting incomplete',
    description: 'Review vesting information before sending disclosures.',
    actionLabel: 'Review Loan',
  },
  {
    id: 'package',
    title: 'Disclosure package unavailable',
    description:
      'No eligible disclosure package was found based on the current loan configuration.',
  },
]

export const complianceFindings: ComplianceFinding[] = [
  {
    id: 'finding-1',
    title: 'Loan Estimate fee tolerance requires review',
    detail:
      'Origination charges increased by $180 since the last issued Loan Estimate. Document a valid changed circumstance or reduce the fee before delivery.',
  },
  {
    id: 'finding-2',
    title: 'Homeownership counseling list is older than 30 days',
    detail:
      'The counseling provider list attached to this loan was generated on Jul 14, 2026. Refresh the list so the delivered package reflects current providers.',
  },
]

export const disclosureDocuments: DisclosureDocument[] = [
  { id: 'd1', name: 'Loan Estimate', category: 'Federal', pages: 5, required: true },
  { id: 'd2', name: 'Intent to Proceed', category: 'Federal', pages: 1, required: true },
  {
    id: 'd3',
    name: 'Settlement Service Provider List',
    category: 'Federal',
    pages: 2,
    required: true,
  },
  { id: 'd4', name: 'Loan Estimate Fee Detail', category: 'Federal', pages: 2, required: true },
  {
    id: 'd5',
    name: 'Truth in Lending Disclosure',
    category: 'Federal',
    pages: 2,
    required: true,
  },
  { id: 'd6', name: 'Your Home Loan Toolkit', category: 'Federal', pages: 6, required: true },
  {
    id: 'd7',
    name: 'Affiliated Business Arrangement Disclosure',
    category: 'Company',
    pages: 2,
    required: true,
  },
  { id: 'd8', name: 'Privacy Policy', category: 'Company', pages: 2, required: true },
  {
    id: 'd9',
    name: 'Authorization to Release Information',
    category: 'Company',
    pages: 1,
    required: false,
  },
  { id: 'd10', name: 'ECOA / Fair Credit Notice', category: 'Federal', pages: 1, required: true },
  {
    id: 'd11',
    name: 'Appraisal Notice & Delivery Acknowledgement',
    category: 'Federal',
    pages: 1,
    required: true,
  },
  {
    id: 'd12',
    name: 'Uniform Residential Loan Application',
    category: 'Federal',
    pages: 6,
    required: true,
  },
  { id: 'd13', name: 'State Disclosure — Colorado', category: 'State', pages: 3, required: true },
  {
    id: 'd14',
    name: 'Colorado Tangible Net Benefit Disclosure',
    category: 'State',
    pages: 2,
    required: true,
  },
  {
    id: 'd15',
    name: 'Servicing Disclosure Statement',
    category: 'Federal',
    pages: 1,
    required: true,
  },
  { id: 'd16', name: 'Escrow Waiver Request', category: 'Company', pages: 1, required: false },
  {
    id: 'd17',
    name: 'Homeownership Counseling Notice',
    category: 'Federal',
    pages: 2,
    required: true,
  },
  { id: 'd18', name: 'Credit Score Disclosure', category: 'Federal', pages: 2, required: true },
  {
    id: 'x1',
    name: 'Preliminary Closing Disclosure',
    category: 'Federal',
    pages: 3,
    required: false,
    excluded: true,
    excludedReason: 'Not eligible at initial disclosure',
  },
  {
    id: 'x2',
    name: 'Rate Lock Agreement',
    category: 'Investor',
    pages: 1,
    required: false,
    excluded: true,
    excludedReason: 'Rate not locked',
  },
]

export const disclosureHistory: DisclosureHistoryEntry[] = [
  {
    id: 'h1',
    title: 'Initial Disclosures',
    status: 'Superseded',
    sentAt: 'Aug 12, 2026 · 10:13 AM',
    pairLabel: 'John Smith + Jane Smith',
    documentCount: 17,
    delivery: 'Electronic',
  },
]

const errors: Record<IntegrationErrorKind, IntegrationError> = {
  loan_locked: {
    kind: 'loan_locked',
    title: 'This loan is currently being edited',
    message:
      "The disclosure package can't be generated until the loan is released by the other user.",
    actionLabel: 'Try Again',
    technicalDetails:
      'POST /encompass/v3/loans/{loanId}/documentOrders → 409 CONFLICT\ncode: LOAN_LOCKED\nlockedBy: mcarter@lender.com (session 8f21)\nretryAfter: 30s',
  },
  already_sent: {
    kind: 'already_sent',
    title: 'Disclosures Already Sent',
    message:
      'This borrower pair has already received all currently available disclosures.',
    actionLabel: 'View Disclosure History',
    technicalDetails:
      'POST /encompass/v3/documentOrders/{orderId}/send → 422 UNPROCESSABLE\ncode: PACKAGE_ALREADY_DELIVERED\nlastDeliveryUtc: 2026-08-18T20:46:11Z',
  },
  no_package: {
    kind: 'no_package',
    title: 'No Disclosure Package Available',
    message: 'No eligible disclosure package was found for this borrower pair.',
    actionLabel: 'Review Loan',
    technicalDetails:
      'POST /encompass/v3/loans/{loanId}/openingDocAudit → 200 OK\npackages: []\naudit.warnings: ["NO_ELIGIBLE_PLAN_CODE"]',
  },
  connection: {
    kind: 'connection',
    title: 'Unable to Connect',
    message: "We couldn't communicate with the document service.",
    actionLabel: 'Try Again',
    technicalDetails:
      'POST /oauth2/v1/token → 401 UNAUTHORIZED\ncode: INVALID_CLIENT_CREDENTIALS\ncorrelationId: 4c9d-11ab-77e2',
  },
}

export function getIntegrationError(kind: IntegrationErrorKind): IntegrationError {
  return errors[kind]
}
